/**
 * <btg-signup> — captarea emailului.
 *
 * ISTORIC, ca să nu se refacă greșeala: prima versiune trimitea formularul cu
 * fetch, ca să nu se reîncarce pagina. Shopify răspunde cu 403 („Enable
 * JavaScript and cookies to continue") la POST-uri /contact venite prin
 * XHR — protecție anti-bot. Deci trimiterea se face NATIV, ca în orice temă
 * Shopify, iar starea de succes o randează Liquid după reîncărcare.
 *
 * JavaScript-ul de aici face patru lucruri mărunte:
 *   1. ține minte CARE formular a fost trimis (pe pagina de pornire sunt
 *      două chaptere blocate, iar Liquid le-ar declara pe amândouă reușite);
 *   2. pune cookie-ul de acces, ca pagina de lectură să știe că a cerut;
 *   3. dacă revine cu cookie-ul deja pus, sare direct la starea de succes —
 *      nu-i mai cere adresa a doua oară (altfel fiecare vizită ar trimite
 *      din nou emailul de confirmare);
 *   4. dacă vine dintr-un link din email (?din=email), pune cookie-ul pe loc:
 *      faptul că a primit emailul dovedește că e deja pe listă. Nu e o
 *      parolă — cine trimite linkul mai departe îl poate folosi și el, dar
 *      poarta oricum nu apără nimic (povestea e gratis).
 *
 * Fără JavaScript totul funcționează în continuare: se înscrie, primește
 * emailul, vede confirmarea. Doar că pe pagina de pornire s-ar bucura ambele
 * carduri, iar recunoașterea automată nu se întâmplă. Preferăm asta unei
 * înscrieri care eșuează.
 *
 * Fișierul se include (script clasic, nu modul) din mai multe secțiuni care
 * pot apărea împreună pe aceeași pagină (subsolul + produs/chapter/lectură/
 * acces-poveste/parolă) — tag-ul <script src> apare de două ori, iar un
 * script clasic se re-execută la fiecare apariție. Tot conținutul e învelit
 * într-un guard: la a doua trecere, elementul e deja definit și blocul e
 * sărit, fără nicio redeclarare de const/class.
 */
if (!customElements.get('btg-signup')) {
  const CHEIE = 'btg-ultimul-formular';

  class BtgSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', () => this.#remember());

      this.#unlockFromLink();

      if (this.#hasCookie()) {
        this.#reveal();
      } else {
        this.#afterReload();
      }
    }

    get key() {
      return this.dataset.cheie || this.id || 'btg';
    }

    get cookieName() {
      return this.dataset.cookie || '';
    }

    /** Înainte de trimiterea nativă, notăm cine a fost. */
    #remember() {
      try {
        sessionStorage.setItem(CHEIE, this.key);
      } catch {
        // Navigare privată — mergem mai departe fără rafinament.
      }
    }

    #hasCookie() {
      const name = this.cookieName;
      if (!name) return false;
      return document.cookie.split('; ').some((c) => c.startsWith(`${name}=`));
    }

    /**
     * Linkul-cheie: cine deschide un link din emailul nostru a fost deja pe
     * listă — parametrul o scutește de-al doilea formular.
     */
    #unlockFromLink() {
      const name = this.cookieName;
      if (!name) return;
      const params = new URLSearchParams(location.search);
      if (params.get('din') !== 'email') return;
      document.cookie = `${name}=1; path=/; max-age=31536000; SameSite=Lax`;
    }

    /** După reîncărcare: Liquid a marcat succesul, noi decidem pe cine îl arătăm. */
    #afterReload() {
      const success = this.querySelector('[data-btg-success]');
      if (!success || success.hidden) return; // Liquid nu a declarat succes aici

      let last = null;
      try {
        last = sessionStorage.getItem(CHEIE);
      } catch {
        // idem
      }

      // Dacă știm că a fost trimis alt formular, ne dăm la o parte.
      if (last && last !== this.key) {
        success.hidden = true;
        const fields = this.querySelector('[data-btg-fields]');
        if (fields) fields.hidden = false;
        if (this.form) this.form.hidden = false;
        return;
      }

      try {
        sessionStorage.removeItem(CHEIE);
      } catch {
        // idem
      }

      const name = this.cookieName;
      if (name) {
        // Un an. Nu ține date personale — doar faptul că a cerut volumul.
        document.cookie = `${name}=1; path=/; max-age=31536000; SameSite=Lax`;
      }

      this.#reveal({ focus: true });
    }

    /** Arată direct starea de succes — la revenire sau prin linkul-cheie. */
    #reveal({ focus = false } = {}) {
      const success = this.querySelector('[data-btg-success]');
      if (!success) return;

      const fields = this.querySelector('[data-btg-fields]');
      if (fields) fields.hidden = true;
      success.hidden = false;

      if (focus) {
        success.setAttribute('tabindex', '-1');
        success.focus({ preventScroll: true });
        success.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }

      this.dispatchEvent(new CustomEvent('btg:signup', { detail: { cookie: this.cookieName || null }, bubbles: true }));
    }
  }

  customElements.define('btg-signup', BtgSignup);
}
